"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import {
  SUPPORT_API_BASE_URL,
  getAdminTickets,
  getTicketDetails,
  getAgents,
  assignTicketToAgent,
  updateTicketStatus,
  getProductSections,
  flattenProductSections,
  getAdminLiveChatSessions,
  getLiveChatSessionDetails,
  getWorkflowTags,
  setTicketTags,
  setChatTags,
  getTicketNotes,
  addTicketNote,
  getChatNotes,
  addChatNote,
  getTicketDuplicates,
  mergeTicket,
  ENTITY_TAGS_MAX,
  NOTE_MAX,
  readApiError,
} from "./supportApi";

import {
  MessageCircle,
  Clock,
  AlertCircle,
  CheckCircle,
  RefreshCw,
  GitMerge,
} from "lucide-react";
import { useSelector } from "react-redux";
import SupportConfirmationModal from "./SupportConfirmationModal";

/**
 * How a chat's status is shown wherever one appears.
 *
 * A session has seven states, not four: the three ticket_* ones are what the bot moves a
 * chat into when nobody is online to take it, and they used to fall through to the raw
 * column value — a reader saw "ticket_details_pending". They are grouped as one amber
 * "offered a ticket", because from the outside they are one situation: the customer is
 * being walked through raising a ticket instead of being answered.
 */
const CHAT_STATUS_BADGES = {
  active: { className: "bg-emerald-500/10 text-emerald-400", text: "Active" },
  pending: { className: "bg-yellow-500/10 text-yellow-400", text: "Pending" },
  agent_handling: {
    className: "bg-ginox-green/10 text-ginox-green",
    text: "Agent Handling",
  },
  closed: { className: "bg-white/[0.06] text-muted-foreground", text: "Closed" },
  // The bot's ticket flow, in the order a chat moves through it: offered a ticket because
  // nobody was online, the ticket created, then waiting on the customer for detail.
  ticket_offered: {
    className: "bg-amber-500/10 text-amber-400",
    text: "Ticket Offered",
  },
  ticket_details_offered: {
    className: "bg-amber-500/10 text-amber-400",
    text: "Ticket Created",
  },
  ticket_details_pending: {
    className: "bg-amber-500/10 text-amber-400",
    text: "Awaiting Details",
  },
};

/** In flow order, which is also how the filter reads. */
const CHAT_STATUS_OPTIONS = [
  "active",
  "pending",
  "agent_handling",
  "ticket_offered",
  "ticket_details_offered",
  "ticket_details_pending",
  "closed",
];

const chatStatusBadge = (status) =>
  CHAT_STATUS_BADGES[status?.toLowerCase()] || {
    className: "bg-white/[0.06] text-muted-foreground",
    text: status || "Unknown",
  };

export function SupportDashboard({
  initialConversations = [],
  conversationStates = [],
  agents = [],
}) {
  // Simple translation function (English only)
  const t = (key) => key;
  const currentLanguage = "en";

  // Simple date/number formatters
  const formatDateTime = (date) => {
    if (!date) return "N/A";
    return new Date(date).toLocaleString("en-US");
  };
  const formatNumber = (num) => {
    if (num === undefined || num === null) return "0";
    return num.toLocaleString("en-US");
  };

  const [conversations, setConversations] = useState(
    initialConversations || [],
  );
  const [states, setStates] = useState(conversationStates || []);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [newMessage, setNewMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [loadingTicketDetail, setLoadingTicketDetail] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState("");
  const [selectedPriority, setSelectedPriority] = useState("");
  const [selectedAssigned, setSelectedAssigned] = useState("");
  const [agentsData, setAgentsData] = useState(agents || []);
  const [productSections, setProductSections] = useState([]);
  const refreshAgents = () => {
    // Fetch agents from API
    fetchAgents();
  };
  const [categories, setCategories] = useState([]);
  const [liveChatConversations, setLiveChatConversations] = useState([]);
  const [isLoadingLiveChat, setIsLoadingLiveChat] = useState(false);
  const [liveChatError, setLiveChatError] = useState(null);
  const [refreshingConversation, setRefreshingConversation] = useState({});
  const [selectedLiveChatId, setSelectedLiveChatId] = useState(null);
  const [selectedLiveChat, setSelectedLiveChat] = useState(null);
  const [loadingLiveChatDetail, setLoadingLiveChatDetail] = useState(false);

  // Derived: show a subtle header indicator if any conversation is refreshing
  const anyRefreshingLiveChat = useMemo(
    () => Object.values(refreshingConversation || {}).some(Boolean),
    [refreshingConversation],
  );
  const [expandedConversations, setExpandedConversations] = useState({});

  // Ticket pagination and filter states (for admin)
  const [ticketPage, setTicketPage] = useState(1);
  const [ticketLimit] = useState(20);
  const [ticketPagination, setTicketPagination] = useState({
    total: 0,
    page: 1,
    limit: 20,
  });
  const [productSectionFilter, setProductSectionFilter] = useState("all");
  const [agentIdFilter, setAgentIdFilter] = useState("all");
  const [startDateFilter, setStartDateFilter] = useState("");
  const [endDateFilter, setEndDateFilter] = useState("");
  const [ticketUseDates, setTicketUseDates] = useState(false);

  // Free-text ticket search. `searchInput` is what the box shows; `ticketQuery` is what
  // actually goes to the API, debounced — ?q= scans every reply, so one request per
  // keystroke would be wasteful.
  const [searchInput, setSearchInput] = useState("");
  const [ticketQuery, setTicketQuery] = useState("");

  // Workflow tags, notes and duplicates for the open ticket / chat.
  const [workflowTags, setWorkflowTags] = useState([]);
  const [ticketTags, setTicketTagsState] = useState([]);
  const [chatTags, setChatTagsState] = useState([]);
  const [isSavingTags, setIsSavingTags] = useState(false);
  const [notes, setNotes] = useState([]);
  const [noteDraft, setNoteDraft] = useState("");
  const [isSavingNote, setIsSavingNote] = useState(false);
  const [noteSurface, setNoteSurface] = useState("ticket");
  const [duplicates, setDuplicates] = useState([]);
  const [mergeTarget, setMergeTarget] = useState(null);
  const [isMerging, setIsMerging] = useState(false);

  // Live chat pagination and filter states
  const [liveChatPage, setLiveChatPage] = useState(1);
  const [liveChatLimit] = useState(20);
  const [liveChatPagination, setLiveChatPagination] = useState({
    total: 0,
    page: 1,
    limit: 20,
  });
  const [liveChatStatusFilter, setLiveChatStatusFilter] = useState("all");
  const [liveChatProductFilter, setLiveChatProductFilter] = useState("all");

  // Helper function to handle attachment URLs
  const getAttachmentUrl = (url) => {
    if (!url) return "";
    // If URL already starts with http:// or https://, return as-is
    if (url.startsWith("http://") || url.startsWith("https://")) {
      return url;
    }
    // Otherwise, prepend the base URL
    return `${SUPPORT_API_BASE_URL}${url}`;
  };

  // Get user from Redux store or localStorage (support system login)
  const { user } = useSelector((state) => state.auth);
  const supportUser = JSON.parse(localStorage.getItem("supportUser") || "null");

  // Determine role - check supportUser first, then Redux user
  const currentUser = supportUser || user;
  const userRole =
    currentUser?.role === "Super Admin"
      ? "admin"
      : currentUser?.role || "admin";

  // Fetch agents from API
  const fetchAgents = async () => {
    try {
      const data = await getAgents();
      const agentsList = Array.isArray(data?.agents) ? data.agents : [];
      setAgentsData(agentsList);
    } catch (e) {
      console.error("Error fetching agents", e);
    }
  };

  // Fetch product sections from API
  const fetchProductSections = async () => {
    try {
      // Flattened: sub-products are nested under their parent, so the top level alone
      // would leave them out of these filters entirely.
      setProductSections(flattenProductSections(await getProductSections()));
    } catch (e) {
      console.error("Error fetching product sections", e);
    }
  };

  useEffect(() => {
    fetchAgents();
    fetchProductSections();
  }, []);

  // Fetch live chat conversations for agents
  useEffect(() => {
    const fetchLiveChatSessions = async () => {
      if (userRole !== "admin") return;

      setIsLoadingLiveChat(true);
      setLiveChatError(null);

      try {
        const data = await getAdminLiveChatSessions({
          page: liveChatPage,
          limit: liveChatLimit,
          status: liveChatStatusFilter,
          product: liveChatProductFilter,
        });

        const sessions = data.sessions || [];
        setLiveChatConversations(sessions);
        setLiveChatPagination({
          total: data.total || 0,
          page: data.page || 1,
          limit: data.limit || 20,
        });
      } catch (error) {
        console.error("Error fetching live chat sessions:", error);
        setLiveChatError("Failed to load live chat sessions");
      } finally {
        setIsLoadingLiveChat(false);
      }
    };

    fetchLiveChatSessions();
  }, [
    userRole,
    liveChatPage,
    liveChatStatusFilter,
    liveChatProductFilter,
    liveChatLimit,
  ]);

  // Select and open a live chat conversation with loading indicator
  const handleLiveChatClick = async (conversationId) => {
    try {
      setSelectedLiveChatId(conversationId);
      setLoadingLiveChatDetail(true);
      setNoteDraft("");
      loadChatNotes(conversationId);

      const response = await getLiveChatSessionDetails(conversationId);

      if (!response.ok) {
        if (response.status === 404) {
          toast.error("Chat session not found");
          setSelectedLiveChat(null);
          setSelectedLiveChatId(null);
          return;
        }
        throw new Error(`Failed to fetch session details: ${response.status}`);
      }

      const sessionData = await response.json();
      setSelectedLiveChat(sessionData);
      setChatTagsState(toSlugs(sessionData.tags));

      // Update the conversation in the list with the latest data
      setLiveChatConversations((prev) =>
        prev.map((conv) =>
          conv.id === conversationId ? { ...conv, ...sessionData } : conv,
        ),
      );
    } catch (error) {
      console.error("Error fetching live chat details:", error);
      toast.error("Failed to load chat details");
    } finally {
      setLoadingLiveChatDetail(false);
    }
  };

  // Refresh a single live chat conversation and update UI state
  const refreshLiveChatConversation = async (conversationId) => {
    if (!conversationId) return;

    setRefreshingConversation((prev) => ({
      ...prev,
      [conversationId]: true,
    }));

    try {
      // Fetch the specific session details with messages
      const response = await getLiveChatSessionDetails(conversationId);

      if (!response.ok) {
        if (response.status === 404) {
          toast.error("Chat session not found");
          setSelectedLiveChat(null);
          setSelectedLiveChatId(null);
          return;
        }
        throw new Error(`Failed to refresh session: ${response.status}`);
      }

      const sessionData = await response.json();

      // Update selected conversation if it's the current one
      if (selectedLiveChatId === conversationId) {
        setSelectedLiveChat(sessionData);
        setChatTagsState(toSlugs(sessionData.tags));
      }

      // Update the conversation in the list
      setLiveChatConversations((prev) =>
        prev.map((conv) =>
          conv.id === conversationId ? { ...conv, ...sessionData } : conv,
        ),
      );

      toast.success("Chat refreshed successfully");
    } catch (error) {
      console.error("Error refreshing live chat conversation:", error);
      toast.error("Failed to refresh conversation");
    } finally {
      setRefreshingConversation((prev) => ({
        ...prev,
        [conversationId]: false,
      }));
    }
  };

  const [filter, setFilter] = useState("all");

  const fetchTicketDetail = async (ticketId) => {
    setLoadingTicketDetail(true);
    try {
      const data = await getTicketDetails(ticketId);

      // Transform ticket data to match the UI format
      const transformedTicket = {
        id: data.id,
        subject: data.subject,
        description: data.description,
        status: data.status,
        productSection: data.productSection,
        customerUsername: data.customerUsername,
        assignedAgentId: data.assignedAgentId,
        assignedAgent: data.assignedAgent,
        createdAt: data.createdAt,
        updatedAt: data.updatedAt,
        attachments: data.attachments || [],
        replies: data.replies || [],
        // Sent as slugs, returned as { slug, name, color } — flatten for the picker and
        // keep the objects for the coloured pills.
        tags: (data.tags || []).map((t) => t.slug),
        tagObjects: data.tags || [],
        // Set on a ticket that was folded into another one. It stays readable, so without
        // saying so it looks like any other closed ticket.
        mergedIntoId: data.mergedIntoId || null,
        mergedAt: data.mergedAt || null,
      };

      setSelectedConversation(transformedTicket);
      setTicketTagsState(transformedTicket.tags);
    } catch (error) {
      console.error("Error fetching ticket detail:", error);
      toast.error("Failed to load ticket details");
    } finally {
      setLoadingTicketDetail(false);
    }
  };

  const handleTicketClick = (conversation) => {
    const ticketId = conversation.id || conversation._id;
    if (ticketId) {
      // Reset the assigned dropdown when selecting a new ticket
      setSelectedAssigned("");
      // Refresh agents to ensure we have the latest agent data for assignment
      refreshAgents();
      fetchTicketDetail(ticketId);
      setNoteDraft("");
      loadTicketExtras(ticketId);
    }
  };

  const refreshConversations = useCallback(async () => {
    setIsRefreshing(true);
    try {
      // Admin only endpoint
      const data = await getAdminTickets({
        page: ticketPage,
        limit: ticketLimit,
        status: filter,
        productSection: productSectionFilter,
        agentId: agentIdFilter,
        startDate: startDateFilter,
        endDate: endDateFilter,
        q: ticketQuery,
      });

      const conversationsData = data.tickets || [];
      setConversations(conversationsData);
      setTicketPagination({
        total: data.total || 0,
        page: data.page || 1,
        limit: data.limit || 20,
      });
      setStates(
        conversationsData
          .map((conv) => conv.state)
          .filter((state) => Boolean(state)),
      );

      // Update selected conversation if it exists in the new data
      setSelectedConversation((prev) => {
        if (!prev) return null;
        const updated = conversationsData.find((c) => c.id === prev.id);
        if (updated) {
          return {
            ...updated,
            messages:
              prev.messages && prev.messages.length > 0
                ? prev.messages
                : updated.messages || [],
          };
        }
        return prev;
      });

      // If a ticket is selected, refresh its details (including replies)
      setSelectedConversation((prev) => {
        if (prev?.id) {
          // Trigger fetchTicketDetail to get fresh replies
          fetchTicketDetail(prev.id);
        }
        return prev;
      });
    } catch (error) {
      console.error("Error refreshing conversations:", error);
    } finally {
      setIsRefreshing(false);
    }
  }, [
    filter,
    ticketPage,
    ticketLimit,
    productSectionFilter,
    agentIdFilter,
    startDateFilter,
    endDateFilter,
  ]);

  useEffect(() => {
    // Only refresh on initial load and when filter changes
    refreshConversations();
    // Also refresh agents to ensure we have the latest agent data
    refreshAgents();

    // Removed automatic polling - no more setInterval calls
    // Users can manually refresh if needed
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filter,
    ticketPage,
    productSectionFilter,
    agentIdFilter,
    startDateFilter,
    endDateFilter,
    ticketQuery,
  ]);

  // Debounce the search box. Typing straight into `ticketQuery` would fire a request per
  // keystroke, and ?q= reads every reply on every ticket.
  useEffect(() => {
    const timer = setTimeout(() => {
      setTicketQuery(searchInput.trim());
      setTicketPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // The tag list is small and rarely changes, so load it once.
  useEffect(() => {
    const loadTags = async () => {
      try {
        const data = await getWorkflowTags();
        setWorkflowTags(data.tags || []);
      } catch (error) {
        console.error("Error fetching workflow tags:", error);
      }
    };
    loadTags();
  }, []);

  /** Tags come back as objects but are sent as bare slugs. */
  const toSlugs = (tags) =>
    (tags || []).map((t) => (typeof t === "string" ? t : t.slug));

  const loadTicketExtras = useCallback(async (ticketId) => {
    setNoteSurface("ticket");
    try {
      const [notesData, duplicatesData] = await Promise.all([
        getTicketNotes(ticketId),
        getTicketDuplicates(ticketId),
      ]);
      setNotes(notesData.notes || []);
      setDuplicates(duplicatesData.candidates || []);
    } catch (error) {
      console.error("Error loading ticket notes/duplicates:", error);
      setNotes([]);
      setDuplicates([]);
    }
  }, []);

  const loadChatNotes = useCallback(async (sessionId) => {
    setNoteSurface("chat");
    setDuplicates([]);
    try {
      const data = await getChatNotes(sessionId);
      setNotes(data.notes || []);
    } catch (error) {
      console.error("Error loading chat notes:", error);
      setNotes([]);
    }
  }, []);

  const handleToggleTag = async (slug) => {
    if (!selectedConversation?.id) return;
    const next = ticketTags.includes(slug)
      ? ticketTags.filter((s) => s !== slug)
      : [...ticketTags, slug];

    setIsSavingTags(true);
    try {
      // PUT replaces the whole set, so the complete list goes every time.
      const response = await setTicketTags(selectedConversation.id, next);
      if (response.ok) {
        const body = await response.json().catch(() => ({}));
        setTicketTagsState(toSlugs(body.tags) || next);
        refreshConversations();
        return;
      }
      toast.error(await readApiError(response, "Could not update the tags"));
    } catch (error) {
      console.error("Error setting ticket tags:", error);
      toast.error("Could not update the tags. Please try again.");
    } finally {
      setIsSavingTags(false);
    }
  };

  const handleToggleChatTag = async (slug) => {
    if (!selectedLiveChatId) return;
    const next = chatTags.includes(slug)
      ? chatTags.filter((s) => s !== slug)
      : [...chatTags, slug];

    setIsSavingTags(true);
    try {
      const response = await setChatTags(selectedLiveChatId, next);
      if (response.ok) {
        const body = await response.json().catch(() => ({}));
        setChatTagsState(toSlugs(body.tags) || next);
        return;
      }
      toast.error(await readApiError(response, "Could not update the tags"));
    } catch (error) {
      console.error("Error setting chat tags:", error);
      toast.error("Could not update the tags. Please try again.");
    } finally {
      setIsSavingTags(false);
    }
  };

  /** The same picker on both surfaces — only the state behind it differs. */
  const renderTagPicker = (selected, onToggle) => (
    <div className="mt-3 border-t border-border/40 pt-3">
      <div className="mb-1.5 flex items-center justify-between">
        <label className="block text-[10px] font-medium text-muted-foreground">
          Tags
        </label>
        <span className="font-mono text-[10px] tabular-nums text-muted-foreground">
          {selected.length} / {ENTITY_TAGS_MAX}
        </span>
      </div>
      {workflowTags.length === 0 ? (
        <p className="text-[10px] text-muted-foreground">
          No tags yet — add some under Workflow Tags.
        </p>
      ) : (
        <div className="flex flex-wrap gap-1.5">
          {workflowTags.map((tag) => {
            const on = selected.includes(tag.slug);
            const atCap = !on && selected.length >= ENTITY_TAGS_MAX;
            return (
              <button
                key={tag.slug}
                type="button"
                disabled={atCap || isSavingTags}
                onClick={() => onToggle(tag.slug)}
                style={
                  on && tag.color
                    ? {
                        backgroundColor: `${tag.color}22`,
                        borderColor: tag.color,
                        color: tag.color,
                      }
                    : undefined
                }
                className={`rounded-md border px-2 py-0.5 text-[10px] transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                  on
                    ? "border-ginox-green/60 bg-ginox-green/10 text-ginox-green"
                    : "border-border/70 bg-white/[0.03] text-muted-foreground hover:text-foreground"
                }`}
              >
                {tag.name}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );

  const handleAddNote = async () => {
    const content = noteDraft.trim();
    const targetId =
      noteSurface === "chat" ? selectedLiveChatId : selectedConversation?.id;
    if (!content || !targetId) return;

    setIsSavingNote(true);
    try {
      const response =
        noteSurface === "chat"
          ? await addChatNote(targetId, content)
          : await addTicketNote(targetId, content);

      if (response.ok) {
        setNoteDraft("");
        const data =
          noteSurface === "chat"
            ? await getChatNotes(targetId)
            : await getTicketNotes(targetId);
        setNotes(data.notes || []);
        toast.success("Note added");
        return;
      }
      toast.error(await readApiError(response, "Could not add the note"));
    } catch (error) {
      console.error("Error adding note:", error);
      toast.error("Could not add the note. Please try again.");
    } finally {
      setIsSavingNote(false);
    }
  };

  /**
   * `mergeTarget` is the duplicate — the ticket that goes away. The open ticket survives
   * and collects its replies.
   */
  const handleMerge = async () => {
    if (!mergeTarget || !selectedConversation?.id) return;

    setIsMerging(true);
    try {
      const response = await mergeTicket(mergeTarget.id, selectedConversation.id);
      if (response.ok) {
        // { merged: { id, mergedInto }, movedReplies } — saying how many replies came
        // across is the only visible sign the merge did anything.
        const body = await response.json().catch(() => ({}));
        const moved = body.movedReplies ?? 0;
        toast.success(
          moved
            ? `Tickets merged — ${moved} ${moved === 1 ? "reply" : "replies"} moved across`
            : "Tickets merged",
        );
        setMergeTarget(null);
        await loadTicketExtras(selectedConversation.id);
        fetchTicketDetail(selectedConversation.id);
        refreshConversations();
        return;
      }
      // 409 has three distinct causes and the message says which, so show it as-is.
      toast.error(await readApiError(response, "Could not merge the tickets"));
    } catch (error) {
      console.error("Error merging tickets:", error);
      toast.error("Could not merge the tickets. Please try again.");
    } finally {
      setIsMerging(false);
    }
  };

  const filteredConversations = useMemo(() => {
    if (filter === "all") {
      return conversations;
    }
    return conversations.filter((conv) => conv.status === filter);
  }, [conversations, filter]);

  // Filter live chat conversations by current language
  const filteredLiveChatConversations = useMemo(() => {
    // No language filtering needed - show all sessions
    return liveChatConversations;
  }, [liveChatConversations]);

  const getConversationState = (conversationId) => {
    const state = states.find(
      (entry) => entry.conversation_id === conversationId,
    );
    if (state) return state;
    const conversation = conversations.find(
      (conv) => conv.id === conversationId,
    );
    return conversation?.state || null;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "active":
        return "bg-ginox-green/10 text-ginox-green";
      case "escalated":
        return "bg-red-500/10 text-red-400";
      case "resolved":
        return "bg-emerald-500/10 text-emerald-400";
      case "open":
        return "bg-yellow-500/10 text-yellow-400";
      case "pending":
      case "pendind": // Handle typo in API response
        return "bg-yellow-500/10 text-yellow-400";
      case "closed":
        return "bg-white/[0.06] text-muted-foreground";
      default:
        return "bg-white/[0.06] text-muted-foreground";
    }
  };

  const getStepDescription = (step) => {
    const steps = {
      greeting: "Initial greeting",
      name: "Collecting name",
      account_check: "Checking account status",
      account_id: "Getting account ID",
      account_type: "Identifying account type",
      question: "Handling questions",
      resolution_check: "Checking resolution",
      escalated: "Escalated to agent",
    };
    return steps[step] || step;
  };

  const stats = useMemo(() => {
    const total = conversations.length;
    const active = conversations.filter((c) => c.status === "active").length;
    const escalated = conversations.filter(
      (c) => c.status === "escalated",
    ).length;
    const resolved = conversations.filter(
      (c) => c.status === "resolved",
    ).length;
    const open = conversations.filter((c) => c.status === "open").length;
    const pending = conversations.filter(
      (c) => c.status === "pending" || c.status === "pendind",
    ).length;
    const closed = conversations.filter((c) => c.status === "closed").length;
    return { total, active, escalated, resolved, open, pending, closed };
  }, [conversations]);

  return (
    <div className="">
      <div className="flex items-center gap-3 text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground mb-1">
        <span className="h-px w-6 bg-ginox-green" aria-hidden="true" />
        Support
      </div>
      <h1 className="font-serif italic text-[36px] leading-[1.05] text-foreground mb-6">Support <span className="text-gradient-ginox">Tickets</span></h1>
<div className="space-y-4 md:space-y-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-4">
          <div className="rounded-lg border border-border/70 bg-card p-5">
            <div className="mb-3">
              <div className="flex flex-row items-center justify-between space-y-0">
                <h2 className="text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">Total</h2>
                <MessageCircle className="h-3 w-3 md:h-4 md:w-4 text-muted-foreground" aria-hidden="true" />
              </div>
            </div>
            <div>
              <div className="text-lg md:text-2xl font-mono text-foreground">{formatNumber(stats.total)}</div>
            </div>
          </div>
          <div className="rounded-lg border border-border/70 bg-card p-5">
            <div className="mb-3">
              <div className="flex flex-row items-center justify-between space-y-0">
                <h2 className="text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">Active</h2>
                <Clock className="h-3 w-3 md:h-4 md:w-4 text-muted-foreground" aria-hidden="true" />
              </div>
            </div>
            <div>
              <div className="text-lg md:text-2xl font-mono text-foreground">{formatNumber(stats.active)}</div>
            </div>
          </div>
          <div className="rounded-lg border border-border/70 bg-card p-5">
            <div className="mb-3">
              <div className="flex flex-row items-center justify-between space-y-0">
                <h2 className="text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">Escalated</h2>
                <AlertCircle className="h-3 w-3 md:h-4 md:w-4 text-red-400" aria-hidden="true" />
              </div>
            </div>
            <div>
              <div className="text-lg md:text-2xl font-mono text-foreground">{formatNumber(stats.escalated)}</div>
            </div>
          </div>
          <div className="rounded-lg border border-border/70 bg-card p-5">
            <div className="mb-3">
              <div className="flex flex-row items-center justify-between space-y-0">
                <h2 className="text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">Resolved</h2>
                <CheckCircle className="h-3 w-3 md:h-4 md:w-4 text-green-400" aria-hidden="true" />
              </div>
            </div>
            <div>
              <div className="text-lg md:text-2xl font-mono text-foreground">{formatNumber(stats.resolved)}</div>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <input
            type="search"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search subject, description and replies…"
            className="w-full md:w-72 rounded-md border border-border/70 bg-white/[0.03] px-2.5 py-1.5 text-xs text-foreground placeholder:text-muted-foreground/50 focus:border-ginox-green/50 focus:outline-none focus:ring-1 focus:ring-ginox-green/30"
          />
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="w-full md:w-60 rounded-md border border-border/70 bg-white/[0.03] px-2.5 py-1.5 text-xs text-foreground placeholder:text-muted-foreground/50 focus:border-ginox-green/50 focus:outline-none focus:ring-1 focus:ring-ginox-green/30"
          >
            <option value="all">All Conversations</option>
            <option value="open">Open</option>
            <option value="pending">Pending</option>
            <option value="resolved">Resolved</option>
            <option value="closed">Closed</option>
          </select>

          <select
            value={productSectionFilter}
            onChange={(e) => { setProductSectionFilter(e.target.value); setTicketPage(1); }}
            className="w-full md:w-48 rounded-md border border-border/70 bg-white/[0.03] px-2.5 py-1.5 text-xs text-foreground placeholder:text-muted-foreground/50 focus:border-ginox-green/50 focus:outline-none focus:ring-1 focus:ring-ginox-green/30"
          >
            <option value="all">All Sections</option>
            {productSections.map((section) => (
              <option key={section.id} value={section.id}>{section.label}</option>
            ))}
          </select>

          <select
            value={agentIdFilter}
            onChange={(e) => { setAgentIdFilter(e.target.value); setTicketPage(1); }}
            className="w-full md:w-48 rounded-md border border-border/70 bg-white/[0.03] px-2.5 py-1.5 text-xs text-foreground placeholder:text-muted-foreground/50 focus:border-ginox-green/50 focus:outline-none focus:ring-1 focus:ring-ginox-green/30"
          >
            <option value="all">All Agents</option>
            <option value="unassigned">Unassigned</option>
            {agentsData.map((agent) => (
              <option key={agent.id || agent._id} value={agent.id || agent._id}>
                {agent.email}
              </option>
            ))}
          </select>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="ticketUseDates"
              checked={ticketUseDates}
              onChange={(e) => {
                setTicketUseDates(e.target.checked);
                if (!e.target.checked) {
                  setStartDateFilter("");
                  setEndDateFilter("");
                  setTicketPage(1);
                }
              }}
              className="h-4 w-4 rounded border-border/70 accent-ginox-green"
            />
            <label htmlFor="ticketUseDates" className="text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground cursor-pointer whitespace-nowrap">
              Filter by Date
            </label>
          </div>
          {ticketUseDates && (
            <>
              <input
                type="date"
                value={startDateFilter}
                onChange={(e) => { setStartDateFilter(e.target.value); setTicketPage(1); }}
                className="w-full md:w-auto rounded-md border border-border/70 bg-white/[0.03] px-2.5 py-1.5 text-xs text-foreground focus:border-ginox-green/50 focus:outline-none focus:ring-1 focus:ring-ginox-green/30"
              />
              <input
                type="date"
                value={endDateFilter}
                onChange={(e) => { setEndDateFilter(e.target.value); setTicketPage(1); }}
                className="w-full md:w-auto rounded-md border border-border/70 bg-white/[0.03] px-2.5 py-1.5 text-xs text-foreground focus:border-ginox-green/50 focus:outline-none focus:ring-1 focus:ring-ginox-green/30"
              />
            </>
          )}

          <button
            onClick={refreshConversations}
            disabled={isRefreshing}
            className="rounded-md border border-border/70 bg-white/[0.03] px-3 py-1.5 text-xs text-muted-foreground hover:bg-white/[0.05] hover:text-foreground transition-colors flex items-center gap-2 disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${isRefreshing ? "animate-spin" : ""}`} aria-hidden="true" />
            {isRefreshing ? "Refreshing..." : "Refresh"}
          </button>
        </div>

        {/* Pagination */}
        {ticketPagination.total > ticketLimit && (
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-xs text-muted-foreground">
              Showing {conversations.length} of {ticketPagination.total} tickets
              (Page {ticketPagination.page} of{" "}
              {Math.ceil(ticketPagination.total / ticketLimit)})
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setTicketPage(1)}
                disabled={ticketPage === 1}
                className={`px-3 py-1 rounded-md text-xs transition ${
                  ticketPage === 1
                    ? "bg-white/[0.04] text-muted-foreground/50 cursor-not-allowed"
                    : "border border-border/70 bg-white/[0.03] text-muted-foreground hover:bg-white/[0.05] hover:text-foreground"
                }`}
              >
                First
              </button>
              <button
                onClick={() => setTicketPage((prev) => Math.max(1, prev - 1))}
                disabled={ticketPage === 1}
                className={`px-3 py-1 rounded-md text-xs transition ${
                  ticketPage === 1
                    ? "bg-white/[0.04] text-muted-foreground/50 cursor-not-allowed"
                    : "border border-border/70 bg-white/[0.03] text-muted-foreground hover:bg-white/[0.05] hover:text-foreground"
                }`}
              >
                Previous
              </button>
              <span className="px-3 py-1 rounded-md text-xs border border-ginox-green/50 bg-ginox-green/10 text-ginox-green">
                {ticketPage}
              </span>
              <button
                onClick={() =>
                  setTicketPage((prev) =>
                    Math.min(
                      Math.ceil(ticketPagination.total / ticketLimit),
                      prev + 1,
                    ),
                  )
                }
                disabled={ticketPage >= Math.ceil(ticketPagination.total / ticketLimit)}
                className={`px-3 py-1 rounded-md text-xs transition ${
                  ticketPage >= Math.ceil(ticketPagination.total / ticketLimit)
                    ? "bg-white/[0.04] text-muted-foreground/50 cursor-not-allowed"
                    : "border border-border/70 bg-white/[0.03] text-muted-foreground hover:bg-white/[0.05] hover:text-foreground"
                }`}
              >
                Next
              </button>
              <button
                onClick={() =>
                  setTicketPage(Math.ceil(ticketPagination.total / ticketLimit))
                }
                disabled={ticketPage >= Math.ceil(ticketPagination.total / ticketLimit)}
                className={`px-3 py-1 rounded-md text-xs transition ${
                  ticketPage >= Math.ceil(ticketPagination.total / ticketLimit)
                    ? "bg-white/[0.04] text-muted-foreground/50 cursor-not-allowed"
                    : "border border-border/70 bg-white/[0.03] text-muted-foreground hover:bg-white/[0.05] hover:text-foreground"
                }`}
              >
                Last
              </button>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
          <div className="rounded-lg border border-border/70 bg-card p-5">
            <div className="mb-3">
              <h2 className="text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">Conversations</h2>
              <p className="text-xs text-muted-foreground mt-1">Select a conversation to view details</p>
            </div>
            <div>
              <div className="overflow-y-auto max-h-[600px] pr-2 md:pr-4">
                <div className="space-y-3">
                  {isRefreshing ? (
                    <div className="flex flex-col items-center justify-center h-[500px] space-y-4">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-ginox-green mx-auto" />
                      <p className="text-sm text-muted-foreground">Loading tickets...</p>
                    </div>
                  ) : filteredConversations.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-[500px] space-y-4">
                      <MessageCircle className="h-8 w-8 text-muted-foreground" aria-hidden="true" />
                      <p className="text-sm text-muted-foreground">No tickets found</p>
                    </div>
                  ) : (
                    filteredConversations.map((conversation) => {
                      const state = getConversationState(conversation.id);
                      return (
                        <div
                          key={conversation.id}
                          className={`p-4 border rounded-lg cursor-pointer transition-all duration-200 ${
                            selectedConversation?.id === conversation.id
                              ? "border-l-4 border-l-ginox-green border-ginox-green/40 bg-ginox-green/10"
                              : "border-border/70 bg-white/[0.03] hover:bg-white/[0.06]"
                          }`}
                          onClick={() => handleTicketClick(conversation)}
                        >
                          <div className="space-y-2">
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex-1 min-w-0 pr-2">
                                <h3 className="font-semibold text-[10px] md:text-[12px] break-words line-clamp-2 text-foreground">
                                  {conversation.subject ||
                                    conversation.customer_name ||
                                    "Anonymous"}
                                </h3>
                                <p className="text-[8px] md:text-[10px] break-all line-clamp-1 text-muted-foreground">
                                  {conversation.customerUsername ||
                                    conversation.customer_username ||
                                    "N/A"}
                                </p>
                              </div>
                              <span className={`shrink-0 text-[7px] md:text-[8px] px-1.5 py-0.5 whitespace-nowrap h-fit inline-flex items-center rounded ${getStatusColor(conversation.status)}`}>
                                {conversation.status}
                              </span>
                            </div>

                            <div className="space-y-1.5 text-[8px] md:text-[10px] text-muted-foreground">
                              <div className="grid grid-cols-[auto_1fr] gap-x-1 gap-y-0.5">
                                <span className="font-medium">
                                  Product Section:
                                </span>
                                <span className="break-words">
                                  {conversation.productSection ||
                                    conversation.product_section ||
                                    "N/A"}
                                </span>
                              </div>
                              <div className="grid grid-cols-[auto_1fr] gap-x-1">
                                <span className="font-medium">Assigned:</span>
                                <span className="break-words">
                                  {conversation.assignedAgent?.email ||
                                    "Unassigned"}
                                </span>
                              </div>
                            </div>
                            <div className="flex flex-wrap gap-x-1 text-[8px] md:text-[10px] text-muted-foreground">
                              <span className="font-medium shrink-0">
                                Updated:
                              </span>
                              <span className="break-all">
                                {formatDateTime(
                                  conversation.updatedAt ||
                                    conversation.updated_at,
                                )}
                              </span>
                            </div>
                          </div>

                          {/* Display media if available */}
                          {conversation.media && (
                            <div className="mt-3">
                              <p className="text-[10px] font-medium mb-2 text-muted-foreground">
                                Attachment:
                              </p>
                              <div className=" rounded-lg p-2 ">
                                {/* Check if it's an image */}
                                {conversation.media.match(
                                  /\.(jpg|jpeg|png|gif|webp)$/i,
                                ) ? (
                                  <img
                                    src={getAttachmentUrl(conversation.media)}
                                    alt="Ticket attachment"
                                    className="max-w-32 h-auto max-h-32 rounded-md object-contain"
                                    onError={(e) => {
                                      // Fallback if image fails to load
                                      e.currentTarget.style.display = "none";
                                      const nextElement =
                                        e.currentTarget.nextElementSibling;
                                      if (nextElement) {
                                        nextElement.style.display = "flex";
                                      }
                                    }}
                                  />
                                ) : (
                                  /* For non-image files, show download link */
                                  <div className="flex items-center gap-2 p-1">
                                    <span className="text-sm">📄</span>
                                    <a
                                      href={getAttachmentUrl(
                                        conversation.media,
                                      )}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-[10px] text-muted-foreground hover:text-foreground underline"
                                    >
                                      Download File
                                    </a>
                                  </div>
                                )}
                                {/* Fallback for failed images */}
                                <div
                                  className="items-center gap-2 p-1"
                                  style={{ display: "none" }}
                                >
                                  <span className="text-sm">📄</span>
                                  <a
                                    href={getAttachmentUrl(conversation.media)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-[10px] text-muted-foreground hover:text-foreground underline"
                                  >
                                    View File
                                  </a>
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Display attachments if available */}
                          {conversation.attachments &&
                            conversation.attachments.length > 0 && (
                              <div className="mt-3">
                                <p className="text-[10px] font-medium mb-2 text-muted-foreground">
                                  📎 {conversation.attachments.length}{" "}
                                  Attachment
                                  {conversation.attachments.length > 1
                                    ? "s"
                                    : ""}
                                </p>
                                <div className="flex flex-wrap gap-2">
                                  {conversation.attachments.map(
                                    (attachment, index) => (
                                      <a
                                        key={index}
                                        href={getAttachmentUrl(attachment)}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="block"
                                      >
                                        {attachment.match(
                                          /\.(jpg|jpeg|png|gif|webp)$/i,
                                        ) ? (
                                          <img
                                            src={getAttachmentUrl(attachment)}
                                            alt={`Attachment ${index + 1}`}
                                            className="w-16 h-16 rounded-md object-cover border border-border/70 hover:border-ginox-green/50 transition-colors"
                                          />
                                        ) : (
                                          <div className="w-16 h-16 rounded-md bg-white/[0.04] border border-border/70 hover:border-ginox-green/50 flex items-center justify-center transition-colors">
                                            <span className="text-xl">📄</span>
                                          </div>
                                        )}
                                      </a>
                                    ),
                                  )}
                                </div>
                              </div>
                            )}
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="rounded-lg border border-border/70 bg-card">
            <div className="px-5 pt-5 pb-3">
              <h2 className="text-sm font-semibold text-foreground">Conversations</h2>
              <p className="text-xs text-muted-foreground mt-0.5">View and Respond</p>
            </div>
            <div className="px-5 pb-5">
              {loadingTicketDetail ? (
                <div className="flex flex-col items-center justify-center py-20 space-y-4">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-ginox-green"></div>
                  <div className="text-center text-muted-foreground text-sm">
                    Loading tickets...
                  </div>
                </div>
              ) : selectedConversation ? (
                <div className="space-y-4">
                  {/* Ticket Details */}
                  <div className="space-y-3 p-3 rounded-lg bg-white/[0.03] border border-border/70">
                    <h3 className="text-sm font-semibold text-foreground">
                      {selectedConversation.subject}
                    </h3>

                    {/* A merged ticket is closed and still readable, so nothing else on
                        this panel would tell you its replies now live elsewhere. */}
                    {selectedConversation.mergedIntoId && (
                      <div className="flex items-start gap-2 rounded-md border border-blue-500/20 bg-blue-500/10 p-2.5">
                        <GitMerge
                          className="mt-0.5 h-3.5 w-3.5 shrink-0 text-blue-400"
                          aria-hidden="true"
                        />
                        <p className="text-[10px] leading-relaxed text-blue-300">
                          Merged into another ticket
                          {selectedConversation.mergedAt
                            ? ` on ${formatDateTime(selectedConversation.mergedAt)}`
                            : ""}
                          . Its replies were moved there.
                        </p>
                      </div>
                    )}

                    <div className="grid grid-cols-2 gap-3 text-[10px]">
                      <div>
                        <span className="font-medium text-muted-foreground">
                          Status:
                        </span>
                        <span className={`ml-2 inline-flex items-center rounded px-1.5 py-0.5 text-[8px] ${getStatusColor(selectedConversation.status)}`}>
                          {selectedConversation.status}
                        </span>
                      </div>
                      <div>
                        <span className="font-medium text-muted-foreground">
                          Product Section:
                        </span>
                        <span className="ml-2 text-foreground">
                          {selectedConversation.productSection || "N/A"}
                        </span>
                      </div>
                      <div>
                        <span className="font-medium text-muted-foreground">
                          Customer:
                        </span>
                        <span className="ml-2 text-foreground">
                          {selectedConversation.customerUsername || "N/A"}
                        </span>
                      </div>
                      <div>
                        <span className="font-medium text-muted-foreground">
                          Assigned:
                        </span>
                        <span className="ml-2 text-foreground">
                          {selectedConversation.assignedAgent?.email ||
                            "Unassigned"}
                        </span>
                      </div>
                    </div>

                    {/* Assign to Agent */}
                    <div className="mt-3">
                      <label className="block text-[10px] font-medium text-muted-foreground mb-1">
                        Assign to Agent:
                      </label>
                      <select
                        value={selectedConversation.assignedAgentId || ""}
                        onChange={async (e) => {
                          const agentId = e.target.value;
                          if (!selectedConversation?.id || !agentId) return;
                          try {
                            const response = await assignTicketToAgent(
                              selectedConversation.id,
                              agentId,
                            );
                            if (response.ok) {
                              toast.success("Ticket assigned successfully");
                              fetchTicketDetail(selectedConversation.id);
                              refreshConversations();
                            } else {
                              toast.error(
                                await readApiError(
                                  response,
                                  "Could not assign the ticket",
                                ),
                              );
                            }
                          } catch (error) {
                            console.error("Error assigning ticket:", error);
                            toast.error("Could not assign the ticket. Please try again.");
                          }
                        }}
                        className="w-full rounded-md border border-border/70 bg-white/[0.03] px-2.5 py-1.5 text-xs text-foreground focus:border-ginox-green/50 focus:outline-none focus:ring-1 focus:ring-ginox-green/30"
                      >
                        <option value="">Select an agent</option>
                        {agentsData.length > 0 ? (
                          agentsData.map((agent) => (
                            <option key={agent.id} value={agent.id}>
                              {agent.name} ({agent.email})
                            </option>
                          ))
                        ) : (
                          <option value="no-agents" disabled>
                            No agents available
                          </option>
                        )}
                      </select>
                    </div>

                    {/* Update Status */}
                    <div className="mt-3">
                      <label className="block text-[10px] font-medium text-muted-foreground mb-1">
                        Update Status:
                      </label>
                      <select
                        value={selectedConversation.status?.toLowerCase() || ""}
                        onChange={async (e) => {
                          const status = e.target.value;
                          if (!selectedConversation?.id || !status) return;
                          try {
                            const response = await updateTicketStatus(
                              selectedConversation.id,
                              status,
                            );
                            if (response.ok) {
                              toast.success("Status updated successfully");
                              fetchTicketDetail(selectedConversation.id);
                              refreshConversations();
                            } else {
                              toast.error(
                                await readApiError(
                                  response,
                                  "Could not update the status",
                                ),
                              );
                            }
                          } catch (error) {
                            console.error("Error updating status:", error);
                            toast.error("Could not update the status. Please try again.");
                          }
                        }}
                        className="w-full rounded-md border border-border/70 bg-white/[0.03] px-2.5 py-1.5 text-xs text-foreground focus:border-ginox-green/50 focus:outline-none focus:ring-1 focus:ring-ginox-green/30"
                      >
                        <option value="">Select status</option>
                        <option value="open">Open</option>
                        <option value="pending">Pending</option>
                        <option value="resolved">Resolved</option>
                        <option value="closed">Closed</option>
                      </select>
                    </div>

                    {renderTagPicker(ticketTags, handleToggleTag)}

                    {/* Possible duplicates — hidden entirely when there are none, which is
                        the normal case. */}
                    {duplicates.length > 0 && (
                      <div className="mt-3 rounded-md border border-yellow-500/20 bg-yellow-500/10 p-3">
                        <p className="mb-2 text-[10px] font-medium text-yellow-400">
                          {duplicates.length} possible duplicate
                          {duplicates.length === 1 ? "" : "s"}
                        </p>
                        <div className="space-y-1.5">
                          {duplicates.map((d) => (
                            <div
                              key={d.id}
                              className="flex items-center justify-between gap-2"
                            >
                              <span className="min-w-0 flex-1 truncate text-[10px] text-foreground">
                                {d.subject}
                              </span>
                              <button
                                type="button"
                                onClick={() => setMergeTarget(d)}
                                className="shrink-0 rounded-md border border-border/70 bg-white/[0.03] px-2 py-0.5 text-[10px] text-muted-foreground hover:bg-white/[0.05] hover:text-foreground transition-colors"
                              >
                                Merge in
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="grid grid-cols-2 gap-3 text-[10px] mt-3">
                      <div>
                        <span className="font-medium text-muted-foreground">
                          Created:
                        </span>
                        <span className="ml-2 text-foreground">
                          {formatDateTime(selectedConversation.createdAt)}
                        </span>
                      </div>
                      <div>
                        <span className="font-medium text-muted-foreground">
                          Updated:
                        </span>
                        <span className="ml-2 text-foreground">
                          {formatDateTime(selectedConversation.updatedAt)}
                        </span>
                      </div>
                    </div>

                    <div>
                      <span className="font-medium text-muted-foreground text-[10px]">
                        Description:
                      </span>
                      <p className="mt-1 text-foreground text-[10px] whitespace-pre-wrap">
                        {selectedConversation.description || "No description"}
                      </p>
                    </div>

                    {/* Display attachments in details panel */}
                    {selectedConversation.attachments &&
                      selectedConversation.attachments.length > 0 && (
                        <div className="mt-4">
                          <span className="font-medium text-muted-foreground text-[10px]">
                            Attachments (
                            {selectedConversation.attachments.length}):
                          </span>
                          <div className="flex flex-wrap gap-3 mt-2">
                            {selectedConversation.attachments.map(
                              (attachment, index) => (
                                <a
                                  key={index}
                                  href={getAttachmentUrl(attachment)}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="block"
                                >
                                  {attachment.match(
                                    /\.(jpg|jpeg|png|gif|webp)$/i,
                                  ) ? (
                                    <img
                                      src={getAttachmentUrl(attachment)}
                                      alt={`Attachment ${index + 1}`}
                                      className="w-24 h-24 rounded-md object-cover border border-border/70 hover:border-ginox-green/50 transition-colors cursor-pointer"
                                    />
                                  ) : (
                                    <div className="w-24 h-24 rounded-md bg-white/[0.04] border border-border/70 hover:border-ginox-green/50 flex flex-col items-center justify-center transition-colors cursor-pointer">
                                      <span className="text-2xl mb-1">📄</span>
                                      <span className="text-[8px] text-muted-foreground">
                                        View File
                                      </span>
                                    </div>
                                  )}
                                </a>
                              ),
                            )}
                          </div>
                        </div>
                      )}
                  </div>

                  {/* Internal notes */}
                  <div className="mt-4 p-3 rounded-lg bg-white/[0.03] border border-border/70">
                    <h4 className="text-[11px] font-semibold text-foreground">
                      Internal notes ({notes.length})
                    </h4>
                    <p className="mt-0.5 mb-3 text-[9px] text-muted-foreground">
                      Only staff can see these — the customer never does.
                    </p>

                    {notes.length > 0 && (
                      <div className="max-h-[180px] overflow-y-auto pr-2 mb-3">
                        <div className="space-y-2">
                          {notes.map((note) => (
                            <div
                              key={note.id}
                              className="rounded-md border border-yellow-500/20 bg-yellow-500/[0.06] p-2.5"
                            >
                              <div className="mb-1 flex items-center justify-between">
                                <span className="text-[9px] font-medium text-yellow-400">
                                  {note.authorName || "Unknown"}
                                </span>
                                <span className="text-[9px] text-muted-foreground">
                                  {formatDateTime(note.createdAt)}
                                </span>
                              </div>
                              <p className="whitespace-pre-wrap text-[10px] leading-relaxed text-foreground">
                                {note.content}
                              </p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    <textarea
                      value={noteDraft}
                      onChange={(e) => setNoteDraft(e.target.value)}
                      maxLength={NOTE_MAX[noteSurface]}
                      rows={3}
                      placeholder="Add a note for the team…"
                      className="w-full resize-y rounded-md border border-border/70 bg-white/[0.03] px-2.5 py-2 text-[10px] leading-relaxed text-foreground placeholder:text-muted-foreground/50 focus:border-ginox-green/50 focus:outline-none focus:ring-1 focus:ring-ginox-green/30"
                    />
                    <div className="mt-1.5 flex items-center justify-between">
                      <span className="font-mono text-[9px] tabular-nums text-muted-foreground">
                        {noteDraft.length} / {NOTE_MAX[noteSurface]}
                      </span>
                      <button
                        type="button"
                        onClick={handleAddNote}
                        disabled={isSavingNote || !noteDraft.trim()}
                        className="rounded-md border border-ginox-green/60 bg-ginox-green/10 px-3 py-1 text-[10px] font-medium text-ginox-green hover:bg-ginox-green/20 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        {isSavingNote ? "Saving..." : "Add note"}
                      </button>
                    </div>
                  </div>

                  {/* Replies Section */}
                  {selectedConversation.replies &&
                    selectedConversation.replies.length > 0 && (
                      <div className="mt-4 p-3 rounded-lg bg-white/[0.03] border border-border/70">
                        <h4 className="text-[11px] font-semibold text-foreground mb-3">
                          Replies ({selectedConversation.replies.length})
                        </h4>
                        <div className="h-[200px] overflow-y-auto pr-2">
                          <div className="space-y-2">
                            {selectedConversation.replies.map((reply) => (
                              <div
                                key={reply.id}
                                className={`p-2.5 rounded-md border ${
                                  reply.authorType === "agent"
                                    ? "bg-ginox-green/10 border-ginox-green/30"
                                    : "bg-white/[0.03] border-border/70"
                                }`}
                              >
                                <div className="flex items-center justify-between mb-1">
                                  <span
                                    className={`text-[9px] font-medium ${
                                      reply.authorType === "agent"
                                        ? "text-ginox-green"
                                        : "text-muted-foreground"
                                    }`}
                                  >
                                    {reply.authorType === "agent"
                                      ? "Agent"
                                      : "Customer"}
                                  </span>
                                  <span className="text-[8px] text-muted-foreground/60">
                                    {formatDateTime(reply.createdAt)}
                                  </span>
                                </div>
                                <p className="text-[10px] text-foreground whitespace-pre-wrap">
                                  {reply.content}
                                </p>
                                {/* Reply Attachments */}
                                {reply.attachments &&
                                  reply.attachments.length > 0 && (
                                    <div className="flex flex-wrap gap-2 mt-2">
                                      {reply.attachments.map(
                                        (attachment, index) => (
                                          <a
                                            key={index}
                                            href={getAttachmentUrl(attachment)}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="block"
                                          >
                                            {attachment.match(
                                              /\.(jpg|jpeg|png|gif|webp)$/i,
                                            ) ? (
                                              <img
                                                src={getAttachmentUrl(
                                                  attachment,
                                                )}
                                                alt={`Attachment ${index + 1}`}
                                                className="w-12 h-12 rounded-md object-cover border border-border/70 hover:border-ginox-green/50 transition-colors"
                                              />
                                            ) : (
                                              <div className="w-12 h-12 rounded-md bg-white/[0.04] border border-border/70 hover:border-ginox-green/50 flex items-center justify-center transition-colors">
                                                <span className="text-sm">
                                                  📄
                                                </span>
                                              </div>
                                            )}
                                          </a>
                                        ),
                                      )}
                                    </div>
                                  )}
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                </div>
              ) : (
                <div className="text-center text-muted-foreground py-20 text-sm">
                  Select a conversation to view details
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Live Chat Conversations */}

        <div className="rounded-lg border border-border/70 bg-card mt-6 overflow-hidden">
          <div className="px-5 pt-5 pb-3">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <h2 className="text-sm font-semibold text-foreground">Live Chat Conversations</h2>
                <p className="text-xs text-muted-foreground mt-0.5 flex items-center gap-2">
                  {anyRefreshingLiveChat && (
                    <span className="flex items-center gap-1 text-ginox-green">
                      <RefreshCw className="h-3 w-3 animate-spin" />
                      Refreshing...
                    </span>
                  )}
                </p>
              </div>

              {/* Live Chat Filters */}
              <div className="flex flex-col md:flex-row gap-2">
                <select
                  value={liveChatStatusFilter}
                  onChange={(e) => {
                    setLiveChatStatusFilter(e.target.value);
                    setLiveChatPage(1);
                  }}
                  className="w-full md:w-40 rounded-md border border-border/70 bg-white/[0.03] px-2.5 py-1.5 text-xs text-foreground focus:border-ginox-green/50 focus:outline-none focus:ring-1 focus:ring-ginox-green/30"
                >
                  <option value="all">All Status</option>
                  {/* Every status the API takes, named the way the badges name them. The
                      three ticket_* ones were missing here because the endpoint used to
                      reject them (backend 77ba073). */}
                  {CHAT_STATUS_OPTIONS.map((status) => (
                    <option key={status} value={status}>
                      {CHAT_STATUS_BADGES[status].text}
                    </option>
                  ))}
                </select>

                <select
                  value={liveChatProductFilter}
                  onChange={(e) => {
                    setLiveChatProductFilter(e.target.value);
                    setLiveChatPage(1);
                  }}
                  className="w-full md:w-40 rounded-md border border-border/70 bg-white/[0.03] px-2.5 py-1.5 text-xs text-foreground focus:border-ginox-green/50 focus:outline-none focus:ring-1 focus:ring-ginox-green/30"
                >
                  <option value="all">All Products</option>
                  {productSections.map((section) => (
                    <option key={section.id} value={section.id}>
                      {section.label}
                    </option>
                  ))}
                </select>

                <button
                  onClick={() => {
                    setIsLoadingLiveChat(true);
                    getAdminLiveChatSessions({
                      page: liveChatPage,
                      limit: liveChatLimit,
                      status: liveChatStatusFilter,
                      product: liveChatProductFilter,
                    })
                      .then((data) => {
                        const sessions = data.sessions || [];
                        setLiveChatConversations(sessions);
                        setLiveChatPagination({
                          total: data.total || 0,
                          page: data.page || 1,
                          limit: data.limit || 20,
                        });
                      })
                      .catch((error) => {
                        console.error("Error refreshing live chat:", error);
                        toast.error("Failed to refresh live chat");
                      })
                      .finally(() => setIsLoadingLiveChat(false));
                  }}
                  disabled={isLoadingLiveChat}
                  className="rounded-md border border-ginox-green/60 bg-ginox-green/10 px-3 py-1.5 text-xs font-medium text-ginox-green hover:bg-ginox-green/20 transition-colors disabled:opacity-50 flex items-center gap-2"
                >
                  <RefreshCw
                    className={`h-4 w-4 ${isLoadingLiveChat ? "animate-spin" : ""}`}
                  />
                  {isLoadingLiveChat ? "Refreshing..." : "Refresh"}
                </button>
              </div>
            </div>
          </div>
          <div className="p-3 md:p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
              {/* Left: Conversation List */}
              <div className="w-full min-w-0">
                <div className="h-[300px] md:h-[400px] overflow-y-auto overflow-x-hidden pr-2 md:pr-3">
                  <div className="space-y-2 md:space-y-3">
                    {isLoadingLiveChat ? (
                      <div className="flex flex-col items-center justify-center h-[300px] space-y-4">
                        <RefreshCw className="h-8 w-8 animate-spin text-ginox-green" />
                        <p className="text-sm text-muted-foreground">
                          Loading conversations...
                        </p>
                      </div>
                    ) : liveChatError ? (
                      <div className="flex flex-col items-center justify-center h-[300px] space-y-4">
                        <AlertCircle className="h-8 w-8 text-red-400" aria-hidden="true" />
                        <p className="text-sm text-red-400 text-center">{liveChatError}</p>
                        <button
                          onClick={() => window.location.reload()}
                          className="rounded-md border border-border/70 bg-white/[0.03] px-3 py-1.5 text-xs text-muted-foreground hover:bg-white/[0.05] hover:text-foreground transition-colors"
                        >
                          Retry
                        </button>
                      </div>
                    ) : filteredLiveChatConversations.length === 0 ? (
                      <div className="flex flex-col items-center justify-center h-[300px] space-y-4">
                        <MessageCircle className="h-8 w-8 text-muted-foreground" aria-hidden="true" />
                        <p className="text-sm text-muted-foreground">No chat conversations found</p>
                      </div>
                    ) : (
                      filteredLiveChatConversations.map((conversation) => {
                        const id = conversation.id;

                        const statusBadge = chatStatusBadge(conversation.status);
                        const isSelected = selectedLiveChatId === id;

                        return (
                          <button
                            key={id}
                            onClick={() => handleLiveChatClick(id)}
                            className={`block w-full max-w-full text-left p-2.5 md:p-4 border rounded-lg transition-colors overflow-hidden ${
                              isSelected
                                ? "border-ginox-green/50 bg-ginox-green/[0.06]"
                                : "border-border/70 hover:bg-white/[0.05]"
                            }`}
                          >
                            <div className="flex flex-col gap-2 w-full max-w-full overflow-hidden">
                              {/* Header with name */}
                              <div className="flex items-start justify-between gap-2 w-full">
                                <h3 className="font-semibold text-[11px] md:text-[13px] text-foreground break-words flex-1 min-w-0 overflow-hidden">
                                  {conversation.customerName || "Anonymous User"}
                                </h3>
                                {refreshingConversation[id] && (
                                  <RefreshCw className="h-3 w-3 animate-spin text-ginox-green shrink-0" aria-hidden="true" />
                                )}
                              </div>

                              {/* Email */}
                              <p className="text-[9px] md:text-[10px] text-muted-foreground break-words overflow-hidden">
                                {conversation.customerEmail || "No email"}
                              </p>

                              {/* Product */}
                              {conversation.product && (
                                <p className="text-[9px] md:text-[10px] text-muted-foreground break-words overflow-hidden">
                                  Product: {conversation.product}
                                </p>
                              )}

                              {/* Footer with badge, timestamp and icon */}
                              <div className="flex items-center justify-between pt-2 border-t border-border/40 gap-2 w-full overflow-hidden">
                                <div className="flex items-center gap-2 flex-1 min-w-0 overflow-hidden">
                                  <span className={`inline-flex items-center rounded px-2 py-0.5 text-[8px] ${statusBadge.className} whitespace-nowrap shrink-0`}>
                                    {statusBadge.text}
                                  </span>
                                  <p className="text-[8px] md:text-[9px] text-muted-foreground truncate overflow-hidden">
                                    {formatDateTime(conversation.createdAt)}
                                  </p>
                                </div>
                                <MessageCircle className="h-3 w-3 md:h-4 md:w-4 text-ginox-green/50 shrink-0" aria-hidden="true" />
                              </div>
                            </div>
                          </button>
                        );
                      })
                    )}
                  </div>
                </div>
              </div>

              {/* Right: Conversation Detail */}
              <div className="min-h-[300px] md:min-h-[400px] border border-border/70 rounded-lg p-2 md:p-3 overflow-hidden">
                {loadingLiveChatDetail ? (
                  <div className="flex flex-col items-center justify-center h-[350px] space-y-4">
                    <RefreshCw className="h-8 w-8 animate-spin text-ginox-green" />
                    <p className="text-sm text-muted-foreground">
                      Loading chat...
                    </p>
                  </div>
                ) : !selectedLiveChat ? (
                  <div className="flex flex-col items-center justify-center h-[350px] space-y-2">
                    <MessageCircle className="h-8 w-8 text-muted-foreground" />
                    <p className="text-xs text-muted-foreground">
                      Select a chat to view details
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-semibold text-[12px] text-foreground">
                          {selectedLiveChat.customerName || "Anonymous User"}
                        </h3>
                        <p className="text-[8px] text-muted-foreground/60">
                          {selectedLiveChat.customerEmail || "No email"}
                        </p>
                        <p className="text-[8px] text-muted-foreground/60">
                          ID: {selectedLiveChat.id}
                        </p>
                        {selectedLiveChat.product && (
                          <p className="text-[8px] text-muted-foreground/60">
                            Product: {selectedLiveChat.product}
                          </p>
                        )}
                      </div>
                      <div className="flex flex-col gap-1">
                        {/* Was painted green whatever the status said, so a closed chat
                            read as a live one. */}
                        <span
                          className={`inline-flex items-center rounded px-2 py-0.5 text-[8px] font-medium ${
                            chatStatusBadge(selectedLiveChat.status).className
                          }`}
                        >
                          {chatStatusBadge(selectedLiveChat.status).text}
                        </span>
                        <button
                          onClick={() =>
                            selectedLiveChatId &&
                            refreshLiveChatConversation(selectedLiveChatId)
                          }
                          className="rounded-md border border-border/70 bg-white/[0.03] px-3 py-1.5 text-xs text-muted-foreground hover:bg-white/[0.05] hover:text-foreground transition-colors"
                        >
                          Refresh
                        </button>
                      </div>
                    </div>

                    {renderTagPicker(chatTags, handleToggleChatTag)}

                    {/* Messages */}
                    <div className="h-[180px] md:h-[240px] overflow-y-auto pr-2 md:pr-3 border border-border/70 rounded-md">
                      <div className="space-y-2 p-2">
                        {selectedLiveChat?.messages &&
                        selectedLiveChat.messages.length > 0 ? (
                          selectedLiveChat.messages.map((msg) => {
                            const sender = msg.sender || "unknown";
                            const isCustomer = sender === "customer";
                            const isBot = sender === "bot";
                            const isAgent = sender === "agent";

                            // Define bubble styling based on sender type
                            const bubbleClasses = isAgent
                              ? "bg-ginox-green/10 text-foreground border-ginox-green/30"
                              : isBot
                                ? "bg-blue-500/10 text-foreground border-blue-500/20"
                                : isCustomer
                                  ? "bg-white/[0.04] text-foreground border-border/70"
                                  : "bg-white/[0.04] text-foreground border-border/70";

                            const timestampColor = isAgent
                              ? "text-ginox-green/70"
                              : isBot
                                ? "text-blue-400/70"
                                : "text-muted-foreground/60";

                            return (
                              <div
                                key={msg.id}
                                className={`rounded-md p-2.5 ${bubbleClasses} border`}
                              >
                                <div className="flex items-center justify-between mb-1">
                                  <p className="text-[9px] md:text-[10px] font-semibold text-foreground">
                                    {sender.charAt(0).toUpperCase() +
                                      sender.slice(1)}
                                  </p>
                                  <p className={`text-[8px] md:text-[9px] ${timestampColor}`}>
                                    {formatDateTime(msg.createdAt)}
                                  </p>
                                </div>
                                <p className="text-[10px] md:text-[11px] leading-relaxed text-foreground">
                                  {msg.content}
                                </p>

                                {/* Display attachments if available */}
                                {msg.attachments &&
                                  Array.isArray(msg.attachments) &&
                                  msg.attachments.length > 0 && (
                                    <div className="mt-2 flex flex-wrap gap-2">
                                      {msg.attachments.map(
                                        (attachment, index) => (
                                          <a
                                            key={index}
                                            href={getAttachmentUrl(attachment)}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="block"
                                          >
                                            {attachment.match(
                                              /\.(jpg|jpeg|png|gif|webp)$/i,
                                            ) ? (
                                              <img
                                                src={getAttachmentUrl(
                                                  attachment,
                                                )}
                                                alt={`Attachment ${index + 1}`}
                                                className="w-16 h-16 rounded-md object-cover border border-border/70 hover:border-ginox-green/50 transition-colors"
                                              />
                                            ) : (
                                              <div className="w-16 h-16 rounded-md bg-white/[0.04] border border-border/70 hover:border-ginox-green/50 flex items-center justify-center transition-colors">
                                                <span className="text-xl">
                                                  📄
                                                </span>
                                              </div>
                                            )}
                                          </a>
                                        ),
                                      )}
                                    </div>
                                  )}
                              </div>
                            );
                          })
                        ) : (
                          <div className="text-center text-muted-foreground py-4 text-[10px]">
                            No messages yet
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Live Chat Pagination */}
            {liveChatPagination.total > liveChatLimit && (
              <div className="mt-4 pt-4 border-t border-border/70 flex flex-col md:flex-row items-center justify-between gap-4">
                <p className="text-xs text-muted-foreground">
                  Showing {liveChatConversations.length} of{" "}
                  {liveChatPagination.total} sessions (Page{" "}
                  {liveChatPagination.page} of{" "}
                  {Math.ceil(liveChatPagination.total / liveChatLimit)})
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setLiveChatPage(1)}
                    disabled={liveChatPage === 1}
                    className={`px-3 py-1.5 rounded-md text-xs transition ${
                      liveChatPage === 1
                        ? "rounded-md border border-border/70 px-3 py-1.5 text-xs text-muted-foreground/40 cursor-not-allowed"
                        : "rounded-md border border-border/70 bg-white/[0.03] px-3 py-1.5 text-xs text-muted-foreground hover:bg-white/[0.05] hover:text-foreground transition-colors"
                    }`}
                  >
                    First
                  </button>
                  <button
                    onClick={() =>
                      setLiveChatPage((prev) => Math.max(1, prev - 1))
                    }
                    disabled={liveChatPage === 1}
                    className={`px-3 py-1.5 rounded-md text-xs transition ${
                      liveChatPage === 1
                        ? "rounded-md border border-border/70 px-3 py-1.5 text-xs text-muted-foreground/40 cursor-not-allowed"
                        : "rounded-md border border-border/70 bg-white/[0.03] px-3 py-1.5 text-xs text-muted-foreground hover:bg-white/[0.05] hover:text-foreground transition-colors"
                    }`}
                  >
                    Previous
                  </button>
                  <span className="rounded-md border border-ginox-green/40 bg-ginox-green/10 px-3 py-1.5 text-xs text-ginox-green font-mono">
                    {liveChatPage}
                  </span>
                  <button
                    onClick={() =>
                      setLiveChatPage((prev) =>
                        Math.min(
                          Math.ceil(liveChatPagination.total / liveChatLimit),
                          prev + 1,
                        ),
                      )
                    }
                    disabled={
                      liveChatPage >=
                      Math.ceil(liveChatPagination.total / liveChatLimit)
                    }
                    className={`px-3 py-1.5 rounded-md text-xs transition ${
                      liveChatPage >=
                      Math.ceil(liveChatPagination.total / liveChatLimit)
                        ? "rounded-md border border-border/70 px-3 py-1.5 text-xs text-muted-foreground/40 cursor-not-allowed"
                        : "rounded-md border border-border/70 bg-white/[0.03] px-3 py-1.5 text-xs text-muted-foreground hover:bg-white/[0.05] hover:text-foreground transition-colors"
                    }`}
                  >
                    Next
                  </button>
                  <button
                    onClick={() =>
                      setLiveChatPage(
                        Math.ceil(liveChatPagination.total / liveChatLimit),
                      )
                    }
                    disabled={
                      liveChatPage >=
                      Math.ceil(liveChatPagination.total / liveChatLimit)
                    }
                    className={`px-3 py-1.5 rounded-md text-xs transition ${
                      liveChatPage >=
                      Math.ceil(liveChatPagination.total / liveChatLimit)
                        ? "rounded-md border border-border/70 px-3 py-1.5 text-xs text-muted-foreground/40 cursor-not-allowed"
                        : "rounded-md border border-border/70 bg-white/[0.03] px-3 py-1.5 text-xs text-muted-foreground hover:bg-white/[0.05] hover:text-foreground transition-colors"
                    }`}
                  >
                    Last
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Only show SupportPage for admin users */}
        {/* <SupportPage /> */}
      </div>

      {/* The direction is easy to get backwards, so the message names both tickets and
          which one survives. */}
      <SupportConfirmationModal
        isOpen={Boolean(mergeTarget)}
        onClose={() => setMergeTarget(null)}
        onConfirm={handleMerge}
        title="Merge tickets"
        message={`"${mergeTarget?.subject}" will be merged into "${selectedConversation?.subject}". Its replies move across and it stops being a separate ticket. This cannot be undone.`}
        confirmText="Yes, Merge"
        cancelText="No, Cancel"
        loading={isMerging}
        loadingText="Merging..."
      />
    </div>
  );
}
